import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AudioButton, ExerciseCard } from '@fluent/ui';
import type { Dialogue as DialogueExercise } from '@fluent/types';
import { type PlayHandle, playClip } from '../audio/engine.js';
import './Dialogue.css';

export interface DialogueProps {
  exercise: DialogueExercise;
  locked: boolean;
}

const PAUSE_BETWEEN_LINES_MS = 500;

/**
 * Dialogue — multi-line conversation exercise (Phase 12).
 *
 * Engine semantics: identical to phrase_intro — no-fail, "გავიგე" CTA in
 * the dock dispatches ADVANCE_INTRO. Counts toward neither XP nor
 * accuracy. Tornike's pilot phone playthrough (2026-04-29) flagged that
 * single-voice multi-line dialogues sound like "a quickly read text";
 * this exercise solves that by:
 *
 *   1. Each line on its own row with a colored speaker tag
 *      (Giorgi green, Mariam terracotta).
 *   2. Per-speaker character voice in the audio (Giorgi → echo,
 *      Mariam → alloy; locked DECISIONS.md). Solo phrases keep alloy.
 *   3. Auto-play through all lines on mount, with a 500ms pause between
 *      lines and the active line highlighted.
 *   4. Per-line ▶ to replay individual lines (interrupts the sequence).
 *
 * Audio architecture: plays each line through the shared Web Audio engine
 * (`audio/engine.ts`; the iOS silent-switch fix is `audio/silentSwitch.ts`).
 * The engine is single-clip (a new play supersedes the previous), which suits
 * a sequential read; we keep a token so an in-flight line that's been
 * superseded doesn't advance the sequence. If a clip fails to load we mark
 * the line and continue to the next.
 */
export function Dialogue({ exercise, locked }: DialogueProps) {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [errorIndex, setErrorIndex] = useState<number | null>(null);
  const sequenceTokenRef = useRef(0);
  const handleRef = useRef<PlayHandle | null>(null);
  const pauseTimerRef = useRef<number | null>(null);
  const autoPlayedRef = useRef<string | null>(null);

  const stop = () => {
    sequenceTokenRef.current += 1;
    if (pauseTimerRef.current) {
      window.clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = null;
    }
    handleRef.current?.stop();
    handleRef.current = null;
    setActiveIndex(-1);
    setErrorIndex(null);
  };

  // Play lines [fromIndex..end] sequentially. When `single` is set, play
  // exactly that one line and stop (per-line replay button).
  const playFrom = (fromIndex: number, single = false) => {
    sequenceTokenRef.current += 1;
    const myToken = sequenceTokenRef.current;

    const playOne = (i: number) => {
      if (myToken !== sequenceTokenRef.current) return;
      if (i >= exercise.lines.length) {
        setActiveIndex(-1);
        return;
      }
      setActiveIndex(i);
      setErrorIndex(null);
      const line = exercise.lines[i]!;

      const advance = (markError: boolean) => {
        if (myToken !== sequenceTokenRef.current) return;
        if (markError) setErrorIndex(i);
        if (single) {
          setActiveIndex(-1);
          return;
        }
        pauseTimerRef.current = window.setTimeout(() => {
          if (myToken !== sequenceTokenRef.current) return;
          playOne(i + 1);
        }, PAUSE_BETWEEN_LINES_MS);
      };

      handleRef.current = playClip(line.audio_key, {
        onEnded: () => advance(false),
        onError: () => advance(true),
      });
    };

    playOne(fromIndex);
  };

  const playSequence = (fromIndex = 0) => playFrom(fromIndex, false);

  const playLine = (idx: number) => {
    stop();
    playFrom(idx, true);
  };

  useEffect(() => {
    if (autoPlayedRef.current === exercise.id) return;
    autoPlayedRef.current = exercise.id;
    playSequence(0);
    return () => {
      // Unmount: cancel any in-flight sequence + stop playback.
      sequenceTokenRef.current += 1;
      if (pauseTimerRef.current) {
        window.clearTimeout(pauseTimerRef.current);
        pauseTimerRef.current = null;
      }
      handleRef.current?.stop();
      handleRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exercise.id]);

  return (
    <ExerciseCard kicker={t('lesson.dialogue.kicker')}>
      <div className="fluent-dialogue" aria-disabled={locked || undefined}>
        {exercise.note_ka && (
          <p className="fluent-dialogue__note" lang="ka">
            {exercise.note_ka}
          </p>
        )}
        <ol className="fluent-dialogue__lines">
          {exercise.lines.map((line, idx) => (
            <li
              key={`${exercise.id}-${idx}`}
              className="fluent-dialogue__line"
              data-speaker={line.speaker.toLowerCase()}
              data-active={activeIndex === idx || undefined}
              data-error={errorIndex === idx || undefined}
            >
              <span className="fluent-dialogue__speaker">{line.speaker}</span>
              <div className="fluent-dialogue__bubble">
                <p className="fluent-dialogue__en" lang="en">{line.en}</p>
                <p className="fluent-dialogue__ka" lang="ka">{line.ka}</p>
              </div>
              <AudioButton
                size="sm"
                playing={activeIndex === idx}
                onClick={() => playLine(idx)}
                disabled={locked}
                label={`Play ${line.speaker} — ${line.en}`}
              />
            </li>
          ))}
        </ol>
        <div className="fluent-dialogue__replay">
          <AudioButton
            size="md"
            playing={activeIndex >= 0}
            onClick={() => {
              if (activeIndex >= 0) stop();
              else playSequence(0);
            }}
            label={t('lesson.dialogue.replay_aria')}
          />
          <span className="fluent-dialogue__replay-label">
            {t('lesson.dialogue.replay_label')}
          </span>
        </div>
      </div>
    </ExerciseCard>
  );
}
